import {createApp} from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import axios from 'axios';
import { applySessionHeadersToConfig } from '@/api/axiosSessionAuth';
import { getPublicBackendOrigin } from '@/config/backendBaseUrl';
import { observeServerReleaseFromAxiosHeaders } from '@/utils/appReleaseWatch';
import { UserRole } from '@/constants/roles';
import { Permission } from '@/constants/permissions';
import {
    getUserRoles,
    storeHasRole,
    storeHasAnyRole,
    storeCan,
} from '@/utils/authFromStore';
import { readStoredUiTheme } from '@/constants/uiTheme';

// Vuetify: полный импорт — стабильный порядок CSS с Vue CLI (без autoImport-чанков)
import './styles/app-shell.css'
import './styles/price-layout-toolbar.css'
import './styles/app-chrome.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import 'vuetify/styles'
import './styles/vuetify-dark-contrast.css'
import {createVuetify} from 'vuetify'
import * as components from 'vuetify/components'
import * as vuetifyDirectives from 'vuetify/directives'
import {tooltipNonEagerDirective} from '@/directives/tooltipNonEager'
import {aliases, mdi} from 'vuetify/iconsets/mdi'
import '@mdi/font/css/materialdesignicons.css'

const vuetify = createVuetify({
    components,
    directives: {
        ...vuetifyDirectives,
        tooltip: tooltipNonEagerDirective,
    },
    theme: {
        defaultTheme: readStoredUiTheme(),
        themes: {
            light: {
                colors: {
                    primary: '#1565c0',
                    secondary: '#455a64',
                    /** Карточки, таблицы — чуть светлее полосы навигации */
                    surface: '#ffffff',
                    background: '#f5f5f5',
                    /** Сайдбар и верхние тулбары (темнее surface). Kebab-case — иначе Vuetify genOnColors ломает on-* для тёмной темы. */
                    'app-chrome': '#eceff1',
                    'on-app-chrome': '#212121',
                },
            },
            dark: {
                dark: true,
                /**
                 * По умолчанию в dark — white: лёгкий «туман» на elevated-кнопках/хроме.
                 * Чёрный overlay почти не светлит тёмные поверхности.
                 * Явно дублируем opacity-переменные дефолтной dark-темы Vuetify: при частичном merge
                 * теоретически могли бы «потеряться» и сломать calc() в color-mix у компонентов.
                 */
                variables: {
                    'elevation-overlay-color': '#000000',
                    'high-emphasis-opacity': 1,
                    'medium-emphasis-opacity': 0.75,
                    'disabled-opacity': 0.5,
                },
                colors: {
                    primary: '#5b9cf5',
                    secondary: '#7b8fa0',
                    /** Таблицы / основной «поднятый» контент — светлее полосы навигации */
                    surface: '#242830',
                    background: '#1a1d23',
                    /** Явно: стабильный контраст для `color-mix` у VTable/VDataTable и `v-application` */
                    'on-surface': '#cdd1d8',
                    'on-background': '#c8cdd4',
                    'app-chrome': '#161a21',
                    'on-app-chrome': '#c8cdd4',
                },
            },
        },
    },
    defaults: {
        /** Глобально: иначе сотни тултипов на таблицах монтируют VOverlay сразу (eager по умолчанию). */
        VTooltip: {
            eager: false,
        },
        // Не задавать rounded глобально — ломает круглые icon-кнопки (v-btn icon)
        VBtn: {
            variant: 'elevated',
        },
        VTextField: {
            variant: 'outlined',
            density: 'comfortable',
        },
        VSelect: {
            variant: 'outlined',
            density: 'comfortable',
        },
        VCard: {
            rounded: 'lg',
        },
        /** Без block-стратегии на documentElement (v-overlay-scroll-blocked) — залипание при SPA-навигации. */
        VDialog: {
            scrollStrategy: 'reposition',
        },
    },
    icons: {
        defaultSet: 'mdi',
        aliases,
        sets: {
            mdi,
        },
    },
})
const app = createApp(App);

//Добавим глобальных переменных
app.config.globalProperties.BE_URL = getPublicBackendOrigin();

// Добавляем глобальные методы с правильными типами
app.config.globalProperties.$hasRole = (role: UserRole): boolean =>
    storeHasRole(store, role);

app.config.globalProperties.$hasAnyRole = (roles: UserRole[]): boolean =>
    storeHasAnyRole(store, roles);

app.config.globalProperties.$can = (permission: Permission): boolean =>
    storeCan(store, permission);

app.config.globalProperties.$userRoles = (): UserRole[] => getUserRoles(store);

app.use(store).use(router).use(vuetify).mount('#app');

axios.interceptors.request.use((config) => {
    applySessionHeadersToConfig(config);
    return config;
});

axios.interceptors.response.use(
  (response) => {
    observeServerReleaseFromAxiosHeaders(response.headers);
    return response;
  },
  (error) => {
    if (error.response?.headers) {
      observeServerReleaseFromAxiosHeaders(error.response.headers);
    }
    const status = error.response?.status;
    const reqUrl = String(error.config?.url ?? '');
    const isLoginRequest = reqUrl.includes('userAuthorize');
    // 401 на форме входа — ожидаемая ошибка (неверный пароль и т.д.), не сбрасываем сессию и не редиректим
    if (status === 401 && !isLoginRequest) {
      localStorage.removeItem('userData');
      window.location.href = '/';
    }
    return Promise.reject(error);
  },
);